import { INote } from '@common/interfaces/notes'
import orderBy from 'lodash/orderBy'

/**
 * Recursive tree operations for nested note reply threads.
 *
 * Every reducer that reacts to ADD/UPDATE/DELETE_NOTE_SUCCESS used to look the
 * action's `parentId` up in its TOP-LEVEL list only. That works while threads
 * are one level deep, but a reply-to-a-reply matches no top-level note, so it
 * was silently dropped from the store and vanished from the UI until a refetch.
 * These helpers walk the whole tree instead.
 *
 * All of them preserve object identity for every branch they did not change,
 * and return the ORIGINAL array when nothing matched at all. That is part of the
 * contract, not an optimisation detail:
 *  - the commentary reducers detect "this action belongs to another thread" by
 *    checking whether the array came back unchanged;
 *  - `Array.map`/`filter` always allocate, so a naive recursion would hand every
 *    untouched sibling a new identity on every keystroke and defeat memoisation
 *    across the whole notes panel.
 */

/**
 * `nodes.map(map)`, except the original array is returned when every element
 * mapped to itself. The identity guarantee above depends on this.
 */
const mapPreservingIdentity = (nodes: INote[], map: (node: INote) => INote): INote[] => {
  let didChange = false
  const next = nodes.map((node) => {
    const mapped = map(node)
    if (mapped !== node) didChange = true
    return mapped
  })
  return didChange ? next : nodes
}

/** Rebuilds `node` with new replies, or returns it untouched if they matched. */
const withChildren = (node: INote, children: INote[]): INote =>
  children === node.children ? node : { ...node, children }

/**
 * Reply order. Deliberately identical to `compareReplies` in
 * `server/src/services/notes/threads.ts` — an optimistic insert that sorts
 * differently from the API would visibly reshuffle the thread on the next
 * refetch.
 */
const sortReplies = (replies: INote[]): INote[] =>
  orderBy(replies, ['resolved', 'pinned', 'createdAt'], ['desc', 'asc', 'asc'])

/** Adds `reply` under the note matching its `parentId`, at any depth. */
export const insertReply = (notes: INote[], reply: INote): INote[] => {
  const insert = (nodes: INote[]): INote[] =>
    mapPreservingIdentity(nodes, (node) => {
      if (node.id === reply.parentId) {
        return { ...node, children: sortReplies([...(node.children ?? []), reply]) }
      }
      if (!node.children?.length) return node
      return withChildren(node, insert(node.children))
    })

  return insert(notes)
}

/** Replaces the note with `updated.id`, at any depth, preserving its replies. */
export const updateInTree = (notes: INote[], updated: INote): INote[] => {
  const replace = (nodes: INote[]): INote[] =>
    mapPreservingIdentity(nodes, (node) => {
      if (node.id === updated.id) {
        // The payload's own replies win when the server sent them (Update
        // returns the hydrated thread); otherwise keep what the store holds, so
        // a partial payload cannot wipe out everything below the edited note.
        return { ...node, ...updated, children: updated.children ?? node.children }
      }
      if (!node.children?.length) return node
      return withChildren(node, replace(node.children))
    })

  return replace(notes)
}

/**
 * Removes the note with `id`, at any depth, along with its whole subtree — the
 * server cascades the delete the same way (notes_parent_id_fkey ON DELETE
 * CASCADE), so leaving orphaned replies in the store would show replies that no
 * longer exist.
 */
export const removeFromTree = (notes: INote[], id: string): INote[] => {
  const prune = (nodes: INote[]): INote[] => {
    // Filter only when this level actually holds the note — `filter` always
    // allocates, which would break the identity guarantee for every other level.
    const kept = nodes.some((node) => node.id === id)
      ? nodes.filter((node) => node.id !== id)
      : nodes
    return mapPreservingIdentity(kept, (node) =>
      node.children?.length ? withChildren(node, prune(node.children)) : node,
    )
  }

  return prune(notes)
}

/**
 * Whether `id` names any note in these trees, at any depth.
 *
 * Used to locate the thread a reply belongs to when the payload cannot say so
 * directly: `reply.parent.commentaryId` is null for a reply-to-a-reply, because
 * its parent is another reply rather than a commentary's anchor note.
 */
export const containsNoteId = (notes: INote[], id: string): boolean =>
  notes.some((note) => note.id === id || containsNoteId(note.children ?? [], id))

/**
 * Whether anything below `note` is still unresolved.
 *
 * Drives the collapsed reply view: a resolved reply is normally hidden, but
 * hiding one that still has unresolved replies under it would take active
 * discussion off screen with no way to reach it.
 */
export const hasUnresolvedDescendant = (note: INote): boolean =>
  (note.children ?? []).some((child) => !child.resolved || hasUnresolvedDescendant(child))
